import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./BatchContact.module.scss";
import { Get_Batch_Point_Of_Contact } from "Service/parent";
import useStore from "store";

interface BatchContactItem {
    StudentID: number;
    StudentName: string;
    BatchID: number;
    BatchName: string;
    TutorID: number;
    TeacherName: string;
    RespectiveRMID: number | null;
    RespectiveRMName: string | null;
    RespectiveRMContact: string | null;
    SeniorRMID: number | null;
    SeniorRMName: string | null;
    SeniorRMContact: string | null;
}

const LIMIT = 10;

const BatchContact = ({ autoExpand = false }: { autoExpand?: boolean }) => {
    const [params] = useSearchParams();
    const studentId = params.get("StudentId");
    const [batchContacts, setBatchContacts] = useState<BatchContactItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(autoExpand);
    const [hasFetched, setHasFetched] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const { setSupportModal, setSupportContext } = useStore(
        (store: any) => store
    );

    const token = {
        Authorization: `token ${localStorage.getItem("learn2readToken")}`,
    };

    const getBatchPointOfContact = async (id: string, pageNumber: number) => {
        setLoading(true);
        try {
            const response = await Get_Batch_Point_Of_Contact(
                "POST",
                null,
                token,
                id,
                pageNumber,
                LIMIT
            );
            if (response?.Status === 1) {
                setBatchContacts(response.Results || []);
                setTotalPages(response.Pagination?.TotalPages || 1);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setHasFetched(false);
        setIsOpen(autoExpand);
        setBatchContacts([]);
        setPage(1);
        if (autoExpand && studentId) {
            getBatchPointOfContact(studentId, 1);
            setHasFetched(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentId]);

    useEffect(() => {
        if (isOpen && studentId && hasFetched) {
            getBatchPointOfContact(studentId, page);
        }
    }, [page]);

    const handleToggle = () => {
        if (!isOpen && !hasFetched && studentId) {
            getBatchPointOfContact(studentId, page);
            setHasFetched(true);
        }
        setIsOpen((prev) => !prev);
    };

    const handleContactUs = (batch: BatchContactItem) => {
        const SendTo = [batch.RespectiveRMID, batch.SeniorRMID].filter(
            (id): id is number => id !== null && id !== undefined
        );
        setSupportContext({
            SendTo,
            BatchID: batch.BatchID,
            TutorID: batch.TutorID,
            RMName: batch.RespectiveRMName || batch.SeniorRMName || "",
        });
        setSupportModal("Contact");
    };

    return (
        <div className={styles["Container"]}>
            <div className={styles["header-wrapper"]}>
                <header>
                    <h3>Need Help with your Batch?</h3>
                    <p>
                        Reach out to your respective Relationship Manager (RM) for any queries
                    </p>
                </header>
                {!autoExpand && (
                    <button
                        className={styles["toggle-btn"]}
                        onClick={handleToggle}
                        disabled={!studentId}>
                        {isOpen ? "Hide Point of Contact" : "View Point of Contact"}
                        <span className={styles["icon"]}>
                            {isOpen ? " ▲" : " ▼"}
                        </span>
                    </button>
                )}
            </div>

            {isOpen && (
                <div className={styles["content-area"]}>
                    {loading ? (
                        <div className={styles["info-box"]}>
                            <p>Loading batch point of contact details...</p>
                        </div>
                    ) : batchContacts.length === 0 ? (
                        <div className={styles["info-box"]}>
                            <p>No point of contact details found.</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles["table"]}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Student Name</th>
                                            <th>Teacher Name</th>
                                            <th>Batch Name</th>
                                            <th>Respective RM</th>
                                            <th>Senior RM</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batchContacts.map((batch) => (
                                            <tr key={batch.BatchID}>
                                                <td>{batch.StudentName}</td>
                                                <td>{batch.TeacherName}</td>
                                                <td>{batch.BatchName}</td>
                                                <td>
                                                    {batch.RespectiveRMName || "-"}
                                                    {batch.RespectiveRMContact && (
                                                        <span>
                                                            {batch.RespectiveRMContact}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {batch.SeniorRMName || "-"}
                                                    {batch.SeniorRMContact && (
                                                        <span>{batch.SeniorRMContact}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        className={styles["contact-btn"]}
                                                        onClick={() =>
                                                            handleContactUs(batch)
                                                        }>
                                                        Contact Us
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "1rem",
                                        marginTop: "0.5rem",
                                    }}>
                                    <button
                                        onClick={() =>
                                            setPage((prev) => Math.max(1, prev - 1))
                                        }
                                        disabled={page === 1 || loading}
                                        style={{
                                            padding: "0.5rem 1rem",
                                            backgroundColor:
                                                page === 1 || loading ? "#e0e0e0" : "#8950fc",
                                            color: page === 1 || loading ? "#999" : "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor:
                                                page === 1 || loading
                                                    ? "not-allowed"
                                                    : "pointer",
                                            fontWeight: 500,
                                        }}>
                                        Previous
                                    </button>
                                    <span style={{ fontSize: "0.9rem", color: "#3f4254" }}>
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() =>
                                            setPage((prev) => Math.min(totalPages, prev + 1))
                                        }
                                        disabled={page === totalPages || loading}
                                        style={{
                                            padding: "0.5rem 1rem",
                                            backgroundColor:
                                                page === totalPages || loading
                                                    ? "#e0e0e0"
                                                    : "#8950fc",
                                            color:
                                                page === totalPages || loading
                                                    ? "#999"
                                                    : "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor:
                                                page === totalPages || loading
                                                    ? "not-allowed"
                                                    : "pointer",
                                            fontWeight: 500,
                                        }}>
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default BatchContact;

